import React from 'react';
import { TFunction } from 'react-i18next';
import {
  Text,
  Link,
  CodeSnippet,
  Stack,
  Trans,
} from '@piwikpro/ui-components';
import { App } from '../../../model';

const PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES = {
  REACT_NATIVE: '@platform/command/appInstallation/reactNative',
};

const getReactNativeSnippetCode = (trackingDomain, siteId) => `await PiwikProSdk.init('https://${trackingDomain}', '${siteId}')`;

export const REACT_NATIVE_SNIPPET_INSTALLATION_CODE = `npm install @piwikpro/react-native-piwik-pro-sdk
# or
yarn add @piwikpro/react-native-piwik-pro-sdk`;

export const REACT_NATIVE_SNIPPET_IMPORT_CODE = 'import PiwikProSdk from "@piwikpro/react-native-piwik-pro-sdk";';


interface ReactNativeInstallProps {
  app: App
  trackingDomain: string
  trackingDomainFailed: boolean
  t: TFunction
  handleCopyToClipboard: (result: boolean, productAnalyticsActionType: string) => void
}

const ReactNativeInstall: React.FC<ReactNativeInstallProps> = ({
  app,
  trackingDomain,
  trackingDomainFailed,
  t,
  handleCopyToClipboard,
}) => (
  <Stack vertical>
    <Stack.Item>
      <Text as="p">
        {t('apps.details.installation.react-native.instruction.line1.part1')}
      </Text>
    </Stack.Item>
    <Stack.Item fill>
      <Stack fullWidth>
        <Stack.Item />
        <Stack.Item fill>
          <CodeSnippet
            error={trackingDomainFailed}
            code={REACT_NATIVE_SNIPPET_INSTALLATION_CODE}
            onCopy={result => handleCopyToClipboard(
              result,
              PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.REACT_NATIVE,
            )}
            mode="no-highlight"
          />
        </Stack.Item>
      </Stack>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        {t('apps.details.installation.react-native.new-instruction.line2')}
      </Text>
    </Stack.Item>
    <Stack.Item fill>
      <Stack fullWidth>
        <Stack.Item />
        <Stack.Item fill>
          <CodeSnippet
            error={trackingDomainFailed}
            code={REACT_NATIVE_SNIPPET_IMPORT_CODE}
            onCopy={result => handleCopyToClipboard(
              result,
              PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.REACT_NATIVE,
            )}
            mode="no-highlight"
          />
        </Stack.Item>
      </Stack>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        {t('apps.details.installation.react-native.new-instruction.line3')}
      </Text>
    </Stack.Item>
    <Stack.Item fill>
      <Stack fullWidth>
        <Stack.Item />
        <Stack.Item fill>
          <CodeSnippet
            error={trackingDomainFailed}
            code={getReactNativeSnippetCode(trackingDomain, app.id)}
            onCopy={result => handleCopyToClipboard(
              result,
              PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.REACT_NATIVE,
            )}
            mode="no-highlight"
          />
        </Stack.Item>
      </Stack>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        <Trans
          i18nKey="apps.details.installation.react-native.new-instruction.line4"
          components={[
            <Link href="https://developers.piwik.pro/docs/react-native-sdk">Set up the SDK</Link>,
          ]}
        />
      </Text>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        <Trans
          i18nKey="apps.details.installation.react-native.new-instruction.line5"
          components={[
            <Link href={`/analytics/#/${app.id}/settings/debugger/`}>tracker debugger</Link>,
          ]}
        />
      </Text>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        {t('apps.details.installation.instruction.help.part1')}
        <Link href="https://developers.piwik.pro/en/latest/data_collection/mobile/Piwik_PRO_SDK_for_React_Native.html">
          {t('apps.details.installation.links.installGuide')}
        </Link>
        {t('apps.details.installation.instruction.help.part2')}
        <Link href="http://community.piwik.pro/">
          {t('apps.details.installation.links.community')}
        </Link>
        .
      </Text>
    </Stack.Item>
  </Stack>
);

export default ReactNativeInstall;
